import React, {useEffect} from 'react';
import {
  ActionModal,
  ErrorModal,
  FormInput,
  FileUploadDropzone,
  FilePreviewInformation,
  useErrorHandler,
} from '../../../../shared';
import {useAddProjectDocumentForm} from '../../model';
import {useProjectDocumentUpload} from '../../lib';
import {Text} from '@gravity-ui/uikit';
import styles from './AddProjectDocumentModal.module.scss';

interface AddProjectDocumentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AddProjectDocumentModal = ({open, setOpen}: AddProjectDocumentModalProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    setFieldValue,
  } = useAddProjectDocumentForm({setOpen});

  const {uploadId, fileName, fileSize, fileExtension, isUploading, uploadDocument, resetUpload} =
    useProjectDocumentUpload();

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Документ',
    title: 'Ошибка при добавлении документа',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  // Очистка формы при открытии модального окна
  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
    }
    // eslint-disable-next-line
  }, [open]);

  // Обновляем uploadId и fileExtension в форме когда документ загружен
  useEffect(() => {
    if (uploadId) {
      setFieldValue('fileUploadId', uploadId);
      setFieldValue('fileExtension', fileExtension || '');
    } else {
      setFieldValue('fileUploadId', '');
      setFieldValue('fileExtension', '');
    }
  }, [uploadId, fileExtension, setFieldValue]);

  const handleFileSelect = (file: File) => {
    uploadDocument(file);
  };

  const handleRemoveFile = () => {
    resetUpload();
    setFieldValue('fileUploadId', '');
    setFieldValue('fileExtension', '');
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Добавить документ'}
        textButtonApply={'Добавить'}
        textButtonCancel={'Отменить'}>
        <FormInput
          label="Название"
          placeholder="Название документа"
          name="title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired
          error={touched.title && errors.title}
        />
        <FormInput
          label="Приоритет"
          placeholder="1"
          name="priority"
          type="number"
          value={values.priority.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired
          error={touched.priority && errors.priority}
        />

        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Файл <span style={{color: 'red'}}>*</span>
          </Text>

          {!uploadId && !values.fileUploadId ? (
            <FileUploadDropzone
              onFileSelect={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSize={25 * 1024 * 1024}
              isLoading={isUploading}
            />
          ) : (
            <FilePreviewInformation fileName={fileName} fileSize={fileSize} handleRemoveFile={handleRemoveFile} />
          )}

          {touched.fileUploadId && errors.fileUploadId && (
            <Text variant="caption-2" color="danger" style={{marginTop: '4px'}}>
              {errors.fileUploadId}
            </Text>
          )}
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
